import { useSyncExternalStore } from 'react';

/**
 * Where this browser keeps the (workspace, connection) pairs whose "nothing selected" mark was
 * answered: the owner opened the list there, chose nothing and closed it.
 *
 * A personal SharePoint connection travels to every workspace its owner belongs to, and in most of
 * them it is meant to reach nothing. An empty list already closes it there, so closing the list
 * empty is a decision, not an unfinished setup, and the mark stops asking — in that workspace only.
 *
 * Browser storage is enough for this: it is a per-viewer convenience, not access state. Losing it
 * brings the red dot back and changes nothing about what the agent can reach.
 */
export const NOTHING_SELECTED_DISMISSED_KEY =
  'cti.sharepoint.nothing-selected-dismissed';

const listeners = new Set<() => void>();
let dismissedPairs: ReadonlySet<string> = readDismissedPairs();

/** One pair as stored. */
function pairKey(workspaceId: string, connectionId: string): string {
  return `${workspaceId}:${connectionId}`;
}

/** The stored pairs; storage that is blocked or holds something else reads as none. */
function readDismissedPairs(): ReadonlySet<string> {
  try {
    const parsed: unknown = JSON.parse(
      localStorage.getItem(NOTHING_SELECTED_DISMISSED_KEY) ?? '[]',
    );
    return new Set(
      Array.isArray(parsed)
        ? parsed.filter((pair): pair is string => typeof pair === 'string')
        : [],
    );
  } catch {
    return new Set();
  }
}

/** Stop marking this connection as "nothing selected" in this workspace. */
export function dismissNothingSelected(
  workspaceId: string,
  connectionId: string,
): void {
  const key = pairKey(workspaceId, connectionId);
  if (dismissedPairs.has(key)) return;
  dismissedPairs = new Set([...dismissedPairs, key]);
  persist(dismissedPairs);
  for (const listener of listeners) listener();
}

/**
 * Writes the pairs to storage. Blocked storage is not an error here: the mark stays hidden until
 * the page reloads, which is the most a per-browser convenience can promise.
 */
function persist(pairs: ReadonlySet<string>): void {
  try {
    localStorage.setItem(
      NOTHING_SELECTED_DISMISSED_KEY,
      JSON.stringify([...pairs]),
    );
  } catch {
    return;
  }
}

/** Registers a re-render for whenever a pair is dismissed. */
function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The current pairs, as a snapshot that only changes when one is added. */
function snapshot(): ReadonlySet<string> {
  return dismissedPairs;
}

/** Whether a (workspace, connection) pair's "nothing selected" mark was answered, kept current. */
export function useNothingSelectedDismissed(): (
  workspaceId: string,
  connectionId: string,
) => boolean {
  const pairs = useSyncExternalStore(subscribe, snapshot, snapshot);
  return (workspaceId, connectionId) =>
    pairs.has(pairKey(workspaceId, connectionId));
}
